import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';
import { RefreshCw, AlertTriangle } from 'lucide-react';

const ALL_FIELDS = ['employee_id', 'first_name', 'last_name', 'start_date', 'address', 'address_2', 'city', 'state', 'zip_code', 'homeowner', 'salary', 'date_of_birth', 'email', 'phone'];

const autoMap = (columns) => {
  const lower = columns.map(c => c.toLowerCase());
  const find = (pred) => { const i = lower.findIndex(pred); return i !== -1 ? columns[i] : undefined; };
  const m = {
    first_name: find(c => c.includes('first')),
    last_name: find(c => c.includes('last')),
    start_date: find(c => c.includes('hire') || c.includes('start') || c.includes('date')),
    address: find(c => c === 'address 1' || c === 'address1' || c === 'address'),
    address_2: find(c => c === 'address 2' || c === 'address2' || c.includes('apt') || c.includes('suite') || c.includes('unit')),
    city: find(c => c === 'city'),
    state: find(c => c === 'state'),
    zip_code: find(c => c.includes('zip')),
    homeowner: find(c => c.includes('homeowner') || c === 'owner'),
    salary: find(c => c.includes('salary') || c.includes('wage') || c.includes('compensation')),
    date_of_birth: find(c => c.includes('birth') || c === 'dob'),
    email: find(c => c.includes('email') || c.includes('e-mail')),
    phone: find(c => c.includes('phone') || c.includes('mobile') || c.includes('cell')),
    employee_id: find(c => c === 'employee id' || c === 'employee_id' || c === 'emp id' || c.includes('badge')),
  };
  Object.keys(m).forEach(k => m[k] === undefined && delete m[k]);
  return m;
};

export const RemapDialog = ({ uploadId, open, onOpenChange, onSuccess }) => {
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    if (open && uploadId) {
      loadPreview();
    } else {
      setPreview(null);
      setMapping({});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, uploadId]);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/census/remap/${uploadId}/preview`);
      setPreview(response.data);
      const stored = response.data.column_mapping || {};
      setMapping(Object.keys(stored).length > 0 ? stored : autoMap(response.data.columns));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load stored file');
      onOpenChange(false);
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    if (!mapping.first_name || !mapping.last_name) {
      toast.error('First Name and Last Name must be mapped');
      return;
    }
    setApplying(true);
    try {
      const response = await api.post(`/census/remap/${uploadId}`, { column_mapping: mapping });
      toast.success(response.data.message);
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to apply re-map');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="remap-dialog">
        <DialogHeader>
          <DialogTitle>Re-map Columns</DialogTitle>
          <DialogDescription>
            {preview ? `Fix the column mapping for "${preview.file_name}" (${preview.total_rows} rows). Corrections will be applied to existing employees.` : 'Loading stored file...'}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="spinner" />
          </div>
        )}

        {preview && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {ALL_FIELDS.map((field) => {
                const label = field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                return (
                  <div key={field} className="space-y-2">
                    <Label className="text-sm">{label}</Label>
                    <Select
                      value={mapping[field] || '_none_'}
                      onValueChange={(value) => setMapping({ ...mapping, [field]: value === '_none_' ? undefined : value })}
                    >
                      <SelectTrigger className="bg-background border-input" data-testid={`remap-${field}`}>
                        <SelectValue placeholder="Select column" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none_">None</SelectItem>
                        {preview.columns.map((col) => (
                          <SelectItem key={col} value={col}>{col}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>

            <div>
              <h4 className="text-sm font-medium mb-2">Data Preview</h4>
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="data-table text-sm">
                  <thead>
                    <tr>
                      {preview.columns.slice(0, 8).map((col) => (
                        <th key={col}>{col}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.preview.slice(0, 3).map((row, idx) => (
                      <tr key={idx}>
                        {preview.columns.slice(0, 8).map((col) => (
                          <td key={col} className="text-muted-foreground">
                            {String(row[col] || '').substring(0, 25)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-sm">
              <AlertTriangle className="w-4 h-4 text-yellow-500 mt-0.5 shrink-0" />
              <p className="text-muted-foreground">
                Employees are matched by <span className="text-foreground font-medium">First Name + Last Name + Start Date</span>.
                Mapped fields will be corrected on existing records. If an address changes, the employee's homeowner
                status is reset to <span className="text-yellow-500">pending</span> for re-verification. No employees are added or removed.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={applying}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            disabled={!preview || applying}
            className="bg-[#E31837] hover:bg-[#c41530] text-white"
            data-testid="apply-remap-btn"
          >
            {applying ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Applying...
              </>
            ) : (
              'Apply Re-map'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
